import React, { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { db } from "./firebase";
import { collection, query, where, onSnapshot } from "firebase/firestore";
import InvestorCard from "./components/InvestorCard";

const BrowseInvestor = ({ visitorMode = false }) => {
  const [investors, setInvestors] = useState([]);
  const [statuses, setStatuses] = useState({});
  const [filteredInvestors, setFilteredInvestors] = useState([]);
  const [locationFilter, setLocationFilter] = useState("");
  const [industryFilter, setIndustryFilter] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [liveMessage, setLiveMessage] = useState("");

  const scrollRef = useRef(null);

  // --- Scroll shadow logic ---
  const updateScrollShadows = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 10);
  };

  const scrollByAmount = (amount) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: amount, behavior: "smooth" });
  };

  const handleKeyScroll = (e) => {
    if (e.key === "Home") {
      e.preventDefault();
      scrollRef.current.scrollTo({ left: 0, behavior: "smooth" });
    }
    if (e.key === "End") {
      e.preventDefault();
      scrollRef.current.scrollTo({
        left: scrollRef.current.scrollWidth,
        behavior: "smooth",
      });
    }
  };

  // --- Scroll listener ---
  useEffect(() => {
    updateScrollShadows();
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener("scroll", updateScrollShadows);
    return () => el.removeEventListener("scroll", updateScrollShadows);
  }, []);

  // --- Real-time fetch investors ---
  useEffect(() => {
    const q = query(collection(db, "users"), where("role", "==", "investor"));
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const investorsData = querySnapshot.docs.map((docSnap) => {
        const data = docSnap.data();
        const images = data.profileImages || [];
        return {
          id: docSnap.id,
          ...data,
          profileImages: images,
          isAvailableForDeals: data.isAvailableForDeals ?? false,
        };
      });
      setInvestors(investorsData);
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  // --- Real-time fetch statuses ---
  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, "status"), (querySnapshot) => {
      const statusMap = {};
      querySnapshot.forEach((docSnap) => {
        statusMap[docSnap.id] = docSnap.data();
      });
      setStatuses(statusMap);
    });
    return () => unsubscribe();
  }, []);

  // --- Merge statuses, filter, and sort boosted first ---
  useEffect(() => {
    const filtered = investors
      .map((inv) => {
        const status = statuses[inv.id] || {};
        return {
          ...inv,
          isOnline: status.isOnline || false,
          lastSeen: status.lastSeen || null,
          isAvailableForDeals: inv.isAvailableForDeals ?? false,
        };
      })
      .filter((inv) => {
        const location = inv.location?.toLowerCase() || "";
        const industry = inv.industry?.toLowerCase() || "";
        const name = inv.name?.toLowerCase() || "";

        // ✅ Match only if filter has input
        const matchLocation = locationFilter ? location.includes(locationFilter.toLowerCase()) : true;
        const matchIndustry = industryFilter ? industry.includes(industryFilter.toLowerCase()) : true;
        const matchName = searchTerm ? name.includes(searchTerm.toLowerCase()) : true;

        return matchLocation && matchIndustry && matchName;
      })
      .sort((a, b) => {
        const aBoost = a.boostExpiresAt?.seconds || 0;
        const bBoost = b.boostExpiresAt?.seconds || 0;
        return bBoost - aBoost; // Boosted first
      });

    setFilteredInvestors(filtered);
  }, [locationFilter, industryFilter, searchTerm, investors, statuses]);

  return (
    <div className="p-6 w-full max-w-7xl mx-auto">
      <h1 className="text-3xl font-bold mb-6 text-center">🌍 Browse Investors</h1>

      {/* Filters */}
      <div className="flex gap-4 mb-6 flex-wrap justify-center">
        <input
          type="text"
          placeholder="📍 Location"
          value={locationFilter}
          onChange={(e) => setLocationFilter(e.target.value)}
          className="border p-2 rounded flex-1 min-w-[150px] max-w-[250px] focus:ring-2 focus:ring-blue-400"
        />
        <input
          type="text"
          placeholder="🏭 Industry"
          value={industryFilter}
          onChange={(e) => setIndustryFilter(e.target.value)}
          className="border p-2 rounded flex-1 min-w-[150px] max-w-[250px] focus:ring-2 focus:ring-blue-400"
        />
        <input
          type="text"
          placeholder="🔍 Search by name"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="border p-2 rounded flex-1 min-w-[150px] max-w-[250px] focus:ring-2 focus:ring-blue-400"
        />
      </div>

      {/* Investor Cards */}
      <AnimatePresence>
        {loading ? (
          <div className="flex flex-col items-center justify-center my-20">
            <div className="w-12 h-12 border-4 border-blue-500 border-dashed rounded-full animate-spin mb-4"></div>
            <p className="text-gray-600 font-semibold">⏳ Loading investors...</p>
          </div>
        ) : filteredInvestors.length === 0 ? (
          <p className="text-center text-gray-500 font-medium">🚫 No investors found.</p>
        ) : (
          <div className="relative">
            <button
              onClick={() => scrollByAmount(-600)}
              disabled={!canScrollLeft}
              className={`hidden md:flex items-center justify-center absolute left-2 top-1/2 -translate-y-1/2 z-20 h-10 w-10 rounded-full bg-white/90 hover:bg-white shadow-md border border-gray-200 transition-opacity ${
                canScrollLeft ? "opacity-100" : "opacity-0 pointer-events-none"
              }`}
            >
              ‹
            </button>

            {canScrollLeft && (
              <div className="pointer-events-none absolute left-0 top-0 h-full w-12 bg-gradient-to-r from-white to-transparent hidden md:block z-10" />
            )}
            {canScrollRight && (
              <div className="pointer-events-none absolute right-0 top-0 h-full w-12 bg-gradient-to-l from-white to-transparent hidden md:block z-10" />
            )}

            <motion.div
              layout
              ref={scrollRef}
              onKeyDown={handleKeyScroll}
              tabIndex={0}
              role="region"
              aria-label="Investor list"
              className="horizontal-scroll flex flex-nowrap gap-6 overflow-x-auto scroll-smooth pb-4 px-12 whitespace-nowrap snap-x snap-mandatory snap-always"
            >
              {filteredInvestors.map((inv, index) => (
                <motion.div
                  key={inv.id}
                  initial={{ opacity: 0, y: -60, scale: 0.8 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -30, scale: 0.9 }}
                  transition={{ duration: 0.5, delay: index * 0.08, ease: "easeOut" }}
                  className="flex flex-col items-center flex-none snap-center w-[140px]"
                >
                  <InvestorCard
                    investor={inv}
                    visitorMode={visitorMode}
                    showThumbnail={true}
                    compact={true}
                  />
                </motion.div>
              ))}
            </motion.div>

            <button
              onClick={() => scrollByAmount(600)}
              disabled={!canScrollRight}
              className={`hidden md:flex items-center justify-center absolute right-2 top-1/2 -translate-y-1/2 z-20 h-10 w-10 rounded-full bg-white/90 hover:bg-white shadow-md border border-gray-200 transition-opacity ${
                canScrollRight ? "opacity-100" : "opacity-0 pointer-events-none"
              }`}
            >
              ›
            </button>

            <div className="sr-only" aria-live="polite">
              {liveMessage}
            </div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default BrowseInvestor;
